import helpers from "../helpers.js";
import { configCheckerOnErrorCallback } from "../configChecker_helpers";
import { setInitialState, ensureBrakesUnlocked } from "./commonSteps.js";
import { MotionMonitor, StepDef } from '../models.js';
import cc_widgets from '../configChecker_widgets.js';
import immediateApi from "../../../api/immediateApi";


const INFINITY = 10000; // what should this value be?
const RUN_UP_DISTANCE = 30; // mm

const checkIsSensorTriggered = (pCheckAfterMove) => new StepDef(({ runtime, actuator, notifier, diagnose, }) => {
  if (runtime.ctx.initialEndStopStates[actuator.driveNumber][runtime.ctx.sensorName] === true) {
    if (runtime.ctx.movedAwayFromSensor) {
      const motorName = actuator.isMultiDrive() ? " parent" : "";
      const sensorPort = runtime.ctx.sensorName + 'port';
      diagnose({
        error: "The " + runtime.ctx.sensorName + " sensor is not connected to the correct SENSOR port.",
        resolution: {
          description: `Ensure the ${runtime.ctx.sensorName} sensor is connected to the${motorName} motor's SENSOR ${actuator[sensorPort]} port. The${motorName} motor is connected to the controller's DRIVE ${actuator.driveNumber} port.`,
        },
        tooltips: [{category: 'sensor'}, {text: `What is SENSOR ${actuator[sensorPort]} port?`, category: 'sensorPort'}],
        errorCode: -1,
      });
      notifier.finished();
    }
    else {
      runtime.runStep(ensureBrakesUnlocked(moveAwayFromSensor(pCheckAfterMove)));
    }
  }
  else {
    runtime.runStep(ensureBrakesUnlocked(move(pCheckAfterMove)));
  }
});

const move = (pCheckAfterMove) => new StepDef(({ runtime, actuator, notifier, pass }) => {

  function sendMotionCommand() {
    let motionMonitorError;
    const lInfoWidget = runtime.ctx.InfoWidget;
    const monitor = new MotionMonitor(actuator, undefined, undefined, (data) => {motionMonitorError = data;} );
    immediateApi.getSmartDriveStatus(actuator.parameters.controllerId)
      .then(() => {
        immediateApi.moveRelative(actuator.parameters.uuid, runtime.ctx.direction * INFINITY, true, false)
          .then(() => {
            monitor.start();
            helpers.loop( repeat => {
              immediateApi.getEndstopState(actuator.parameters.controllerId)
                .then((pEndStopState) => {

                  for (const driveNumber in pEndStopState) {
                    for (const sensor in pEndStopState[driveNumber]) {
                      if (pEndStopState[driveNumber][sensor] && !runtime.ctx.initialEndStopStates[driveNumber][sensor]) {
                        immediateApi.halt(actuator.parameters.uuid).catch(() => console.error('Failed to halt during move.'));
                        monitor.kill();
                        if (runtime.ctx.sensorName === sensor && parseInt(driveNumber) === actuator.driveNumber) {
                          lInfoWidget.update("Motion complete.", false);
                          // Success !
                          runtime.ctx.validatable.isValid = true;
                          pass("The " + runtime.ctx.sensorName + " sensor is correctly installed and connected.");
                          notifier.finished();
                          return;
                        }
                        else { // unexpected
                          lInfoWidget.update("Motion triggered a sensor.", false);
                          runtime.ctx.triggeredEndStop = sensor;
                          runtime.ctx.triggeredDriveNumber = parseInt(driveNumber);
                          return runtime.runStep(pCheckAfterMove); // Note Audrey : The whole pCheckAfterMove chain is just there because of this single call. Maybe there is more elegant way to do this...
                        }
                      }
                    }
                  }

                  if (motionMonitorError != undefined) {
                    immediateApi.halt(actuator.parameters.uuid).catch(() => console.error('Failed to halt during move.'));
                    monitor.kill();
                    console.warn("position not changing");
                    return runtime.runStep(pCheckAfterMove); // try to recover (if we moved a noticeable amount)
                  }

                  repeat();

                }).catch(configCheckerOnErrorCallback);
            }, { delay: 250 });
          }).catch(configCheckerOnErrorCallback);
      })
      .catch(configCheckerOnErrorCallback);

  }

  runtime.ctx.movedTowardsSensor = true;

  if (runtime.ctx.doNotPromptUserForConsent) {
    sendMotionCommand();
  }
  else {
    const lInfoWidget = new cc_widgets.InfoWidget();
    runtime.ctx.InfoWidget = lInfoWidget;
    const ui = cc_widgets.StepWidgets.consentMove({
      title: CTAText => `Press "${CTAText}" to move the actuator "${actuator.parameters.friendlyName}" to the ${runtime.ctx.sensorName} sensor.`,
    },
    () => {
      lInfoWidget.update("Moving the actuator...", true);
      ui.addWidget(lInfoWidget);
      sendMotionCommand();
    });
  }

});

const moveAwayFromSensor = (pCheckAfterMove) => new StepDef(({ runtime, notifier, actuator, }) => {

  function sendMotionCommand() {
    let motionMonitorError;
    const monitor = new MotionMonitor(actuator, undefined, undefined, (data) => {motionMonitorError = data;} );
    immediateApi.getSmartDriveStatus(actuator.parameters.controllerId)
      .then(() => {
        immediateApi.moveRelative(actuator.parameters.uuid, -runtime.ctx.direction * RUN_UP_DISTANCE, true, false)
          .then(() => {
            monitor.start();
            helpers.loop(repeat => {
              immediateApi.isMotionComplete(actuator.parameters.controllerId)
                .then(({isComplete}) => {
                  if (isComplete) {
                    monitor.kill();
                    return runtime.runStep(setInitialState(checkIsSensorTriggered(pCheckAfterMove))); // restart
                  }

                  if (motionMonitorError != undefined) {
                    immediateApi.halt(actuator.parameters.uuid).catch(() => console.error('Failed to halt during move away from sensor.'));
                    monitor.kill();
                    console.warn("crashed");
                    if (runtime.ctx.movedTowardsSensor) { // Question Audrey : Is there a scenario for this ?
                      return runtime.runStep(setInitialState(checkIsSensorTriggered(pCheckAfterMove)));
                    }
                    else {
                      return runtime.runStep(ensureBrakesUnlocked(move(pCheckAfterMove)));
                    }
                  }

                  repeat();
                }).catch(configCheckerOnErrorCallback);
            }, { delay: 250 });
          })
          .catch(configCheckerOnErrorCallback);
      }).catch(configCheckerOnErrorCallback);
  }

  runtime.ctx.movedAwayFromSensor = true;

  if (runtime.ctx.doNotPromptUserForConsent) {
    sendMotionCommand();
  }
  else {
    const lInfoWidget = new cc_widgets.InfoWidget();
    runtime.ctx.InfoWidget = lInfoWidget;
    const ui = cc_widgets.StepWidgets.consentMove({
      title: CTAText => `Press "${CTAText}" to move the actuator "${actuator.parameters.friendlyName}" so that it interacts with the ${runtime.ctx.sensorName} sensor.`,
    },
    () => {
      ui.addWidget(lInfoWidget);
      lInfoWidget.update("Moving the actuator...", true);
      runtime.ctx.doNotPromptUserForConsent = true;
      sendMotionCommand();
    });
  }
});

const diagnoseBadSensor = new StepDef(({ runtime, actuator, notifier, diagnose }) => {
  const motorName = actuator.isMultiDrive() ? " parent" : "";
  const sensorPort = runtime.ctx.sensorName + 'port';
  diagnose({
    error: "The " + runtime.ctx.sensorName + " sensor is not connected to the correct SENSOR port.",
    resolution: {
      description: `Ensure the ${runtime.ctx.sensorName} sensor is connected to the${motorName} motor's SENSOR ${actuator[sensorPort]} port. The${motorName} motor is connected to the controller's DRIVE ${actuator.driveNumber} port.`,
    },
    tooltips: [{category: 'sensor'}, {text: `What is SENSOR ${actuator[sensorPort]} port?`, category: 'sensorPort'}],
    errorCode: -1,
  });
  notifier.finished();
});

export {
  checkIsSensorTriggered,
  moveAwayFromSensor,
  diagnoseBadSensor
};
